import { Campo, Chave, Selecao } from "../../components/ui.js";
import type { EscolhaDeMusica, PreferenciasGaleria } from "./preferencias.js";

type Parte = Pick<PreferenciasGaleria, "musica" | "musicaAleatoria" | "musicaRepetir">;

/**
 * A escolha da música, igual na tela da galeria e no link da TV.
 *
 * Um lugar só para as duas telas porque é a mesma pergunta: sem música, uma
 * playlist já montada, ou as músicas marcadas na hora — e, havendo música, a
 * ordem e se repete. Quem chama passa as listas que aquela sessão enxerga.
 */
export function EscolhaDeMusicaNaGaleria({
  playlists,
  musicas,
  valor,
  aoMudar,
}: {
  playlists: { id: number; nome: string; quantas: number }[];
  musicas: { id: number; titulo: string }[];
  valor: Parte;
  aoMudar: (parcial: Partial<Parte>) => void;
}) {
  const m = valor.musica;
  const selecionado = m.tipo === "playlist" ? `p:${m.id}` : m.tipo;
  const marcadas = m.tipo === "musicas" ? m.ids : [];

  function escolher(v: string) {
    let musica: EscolhaDeMusica = { tipo: "nenhuma" };
    if (v.startsWith("p:")) musica = { tipo: "playlist", id: Number(v.slice(2)) };
    // "Escolher músicas" começa com nada marcado; a TV só toca quando houver alguma.
    if (v === "musicas") musica = { tipo: "musicas", ids: marcadas };
    aoMudar({ musica });
  }

  function alternar(id: number, marcar: boolean) {
    // A ordem é a de marcar: é a que a sequência vai tocar.
    const ids = marcar ? [...marcadas, id] : marcadas.filter((x) => x !== id);
    aoMudar({ musica: { tipo: "musicas", ids } });
  }

  const semMusicas = musicas.length === 0;

  return (
    <div className="flex flex-col gap-3">
      <Campo
        rotulo="Música"
        dica={
          semMusicas
            ? "Ainda não há músicas no sistema: o suporte sobe em Acervo → Músicas."
            : "Toca por baixo das fotos. Divulgação em vídeo com som pausa a música."
        }
      >
        <Selecao value={selecionado} onChange={(e) => escolher(e.target.value)}>
          <option value="nenhuma">Sem música</option>
          {playlists.map((p) => (
            <option key={p.id} value={`p:${p.id}`} disabled={p.quantas === 0}>
              {p.nome} ({p.quantas} {p.quantas === 1 ? "música" : "músicas"})
            </option>
          ))}
          {!semMusicas && <option value="musicas">Escolher as músicas</option>}
        </Selecao>
      </Campo>

      {m.tipo === "musicas" && (
        <fieldset className="flex max-h-56 flex-col gap-1.5 overflow-y-auto rounded-peca border border-borda px-3 py-2">
          <legend className="px-1 text-[0.75rem] text-texto-fraco">
            {marcadas.length === 0
              ? "Marque as que vão tocar"
              : `${marcadas.length} ${marcadas.length === 1 ? "marcada" : "marcadas"}, na ordem em que foram marcadas`}
          </legend>
          {musicas.map((x) => (
            <label key={x.id} className="flex items-center gap-2 py-0.5 text-[0.875rem] text-texto">
              <input
                type="checkbox"
                checked={marcadas.includes(x.id)}
                onChange={(e) => alternar(x.id, e.target.checked)}
                className="size-4 accent-[var(--color-brasa)]"
              />
              <span className="min-w-0 truncate">{x.titulo}</span>
            </label>
          ))}
        </fieldset>
      )}

      {m.tipo !== "nenhuma" && (
        <>
          <Campo rotulo="Ordem">
            <Selecao
              value={valor.musicaAleatoria ? "aleatoria" : "sequencia"}
              onChange={(e) => aoMudar({ musicaAleatoria: e.target.value === "aleatoria" })}
            >
              <option value="sequencia">Na sequência</option>
              <option value="aleatoria">Aleatória</option>
            </Selecao>
          </Campo>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[0.875rem] text-texto">Repetir</p>
              <p className="text-[0.75rem] text-texto-fraco">
                {valor.musicaRepetir
                  ? "No fim, começa de novo"
                  : "No fim, a galeria segue em silêncio"}
              </p>
            </div>
            <Chave
              rotulo="Repetir as músicas"
              ligada={valor.musicaRepetir}
              aoAlternar={(v) => aoMudar({ musicaRepetir: v })}
            />
          </div>
        </>
      )}
    </div>
  );
}
